/**
 * Shared animation engine for Ads Creator.
 * Used by BOTH the in-editor "Preview" button and the MP4 recorder.
 * Supports entry + exit + typewriter effects.
 */

export const ENTRY_TYPES = [
  { id: "fade-in",        label: "Fade In",        color: "#22c55e" },
  { id: "slide-in-left",  label: "Slide ← left",   color: "#3b82f6" },
  { id: "slide-in-right", label: "Slide → right",  color: "#06b6d4" },
  { id: "slide-in-up",    label: "Slide ↑ up",     color: "#ff4d9d" },
  { id: "slide-in-down",  label: "Slide ↓ down",   color: "#a855f7" },
  { id: "zoom-in",        label: "Zoom In",        color: "#f59e0b" },
  { id: "bounce",         label: "Bounce",         color: "#ef4444" },
  { id: "typewriter",     label: "Typewriter",     color: "#eab308" },
];

export const EXIT_TYPES = [
  { id: "fade-out",        label: "Fade Out",       color: "#64748b" },
  { id: "slide-out-left",  label: "Slide out ←",    color: "#475569" },
  { id: "slide-out-right", label: "Slide out →",    color: "#475569" },
  { id: "zoom-out",        label: "Zoom Out",       color: "#6b7280" },
];

export const ALL_TYPES = [...ENTRY_TYPES, ...EXIT_TYPES];

const COLOR_MAP = Object.fromEntries(ALL_TYPES.map((t) => [t.id, t.color]));
export const getAnimColor = (id) => COLOR_MAP[id] || "#7c3aed";

const EXIT_SET = new Set(EXIT_TYPES.map((t) => t.id));
export const isExit = (type) => EXIT_SET.has(type);

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const lerp = (a, b, p) => a + (b - a) * p;

function snapshotObjects(objs) {
  return objs.map((o) => ({
    left: o.left, top: o.top, opacity: o.opacity ?? 1,
    scaleX: o.scaleX ?? 1, scaleY: o.scaleY ?? 1,
    _fullText: o.type === "textbox" ? (o.text ?? "") : undefined,
  }));
}

function resetAll(objs, snapshots) {
  objs.forEach((o, i) => {
    const s = snapshots[i]; if (!s) return;
    o.set({ left: s.left, top: s.top, opacity: s.opacity, scaleX: s.scaleX, scaleY: s.scaleY });
    if (s._fullText !== undefined && o.type === "textbox") o.set({ text: s._fullText });
  });
}

function applyPreState(o, a, snap) {
  if (isExit(a.type)) {
    // Before exit animation starts, object is in its natural final state.
    o.set({ left: snap.left, top: snap.top, opacity: snap.opacity,
            scaleX: snap.scaleX, scaleY: snap.scaleY });
    if (a.type === "typewriter" && snap._fullText !== undefined) o.set({ text: snap._fullText });
    return;
  }
  switch (a.type) {
    case "fade-in":        return o.set({ opacity: 0 });
    case "slide-in-left":  return o.set({ left: snap.left - 200, opacity: 0 });
    case "slide-in-right": return o.set({ left: snap.left + 200, opacity: 0 });
    case "slide-in-up":    return o.set({ top: snap.top + 200, opacity: 0 });
    case "slide-in-down":  return o.set({ top: snap.top - 200, opacity: 0 });
    case "zoom-in":        return o.set({ scaleX: 0.01, scaleY: 0.01, opacity: 0 });
    case "bounce":         return o.set({ scaleX: 1.25, scaleY: 1.25, opacity: 0 });
    case "typewriter":     return o.set({ opacity: snap.opacity, text: "" });
    default:               return o.set({ opacity: 0 });
  }
}

function applyFrame(o, a, snap, p) {
  switch (a.type) {
    case "fade-in":
      return o.set({ opacity: lerp(0, snap.opacity, p) });
    case "slide-in-left":
      return o.set({ left: lerp(snap.left - 200, snap.left, p), opacity: lerp(0, snap.opacity, p) });
    case "slide-in-right":
      return o.set({ left: lerp(snap.left + 200, snap.left, p), opacity: lerp(0, snap.opacity, p) });
    case "slide-in-up":
      return o.set({ top: lerp(snap.top + 200, snap.top, p), opacity: lerp(0, snap.opacity, p) });
    case "slide-in-down":
      return o.set({ top: lerp(snap.top - 200, snap.top, p), opacity: lerp(0, snap.opacity, p) });
    case "zoom-in":
      return o.set({
        scaleX: lerp(0.01, snap.scaleX, p),
        scaleY: lerp(0.01, snap.scaleY, p),
        opacity: lerp(0, snap.opacity, p),
      });
    case "bounce": {
      const b = 1 - Math.abs(Math.sin(p * Math.PI * 1.5)) * (1 - p);
      return o.set({
        scaleX: lerp(1.25, snap.scaleX, p) * b,
        scaleY: lerp(1.25, snap.scaleY, p) * b,
        opacity: lerp(0, snap.opacity, p),
      });
    }
    case "typewriter": {
      const full = snap._fullText ?? "";
      const n = Math.max(0, Math.min(full.length, Math.floor(full.length * p)));
      return o.set({ text: full.slice(0, n), opacity: snap.opacity });
    }
    case "fade-out":
      return o.set({ opacity: lerp(snap.opacity, 0, p) });
    case "slide-out-left":
      return o.set({ left: lerp(snap.left, snap.left - 260, p), opacity: lerp(snap.opacity, 0, p) });
    case "slide-out-right":
      return o.set({ left: lerp(snap.left, snap.left + 260, p), opacity: lerp(snap.opacity, 0, p) });
    case "zoom-out":
      return o.set({
        scaleX: lerp(snap.scaleX, 0.01, p),
        scaleY: lerp(snap.scaleY, 0.01, p),
        opacity: lerp(snap.opacity, 0, p),
      });
    default:
      return o.set({ opacity: lerp(0, snap.opacity, p) });
  }
}

function applyPostState(o, a, snap) {
  if (isExit(a.type)) {
    // After an exit anim completes, keep the final "exited" state until full duration ends.
    switch (a.type) {
      case "fade-out":        return o.set({ opacity: 0 });
      case "slide-out-left":  return o.set({ left: snap.left - 260, opacity: 0 });
      case "slide-out-right": return o.set({ left: snap.left + 260, opacity: 0 });
      case "zoom-out":        return o.set({ scaleX: 0.01, scaleY: 0.01, opacity: 0 });
      default: return;
    }
  }
  // Entry animations settle back to the natural snapshot state
  o.set({ left: snap.left, top: snap.top, opacity: snap.opacity,
          scaleX: snap.scaleX, scaleY: snap.scaleY });
  if (a.type === "typewriter" && snap._fullText !== undefined) {
    o.set({ text: snap._fullText });
  }
}

/**
 * Drive a full-duration animation playback on the Fabric canvas.
 * @param {fabric.Canvas} c
 * @param {Array} animations
 * @param {number} duration total playback length in ms
 * @param {{ onComplete?: () => void, abortRef?: { current: boolean } }} opts
 */
export async function runAnimations(c, animations, duration, opts = {}) {
  if (!c || !animations?.length) return;
  const objs = c.getObjects();
  const snapshots = snapshotObjects(objs);

  // Prime all animated objects to their pre-state at t=0
  animations.forEach((a) => {
    const o = objs[a.object_index]; if (!o) return;
    applyPreState(o, a, snapshots[a.object_index]);
  });
  c.requestRenderAll();

  const t0 = performance.now();
  await new Promise((resolve) => {
    function tick() {
      if (opts.abortRef?.current) { resetAll(objs, snapshots); c.requestRenderAll(); return resolve(); }
      const t = performance.now() - t0;

      animations.forEach((a) => {
        const o = objs[a.object_index]; if (!o) return;
        const snap = snapshots[a.object_index];
        const start = a.start_at, end = a.start_at + a.duration;
        if (t < start)           applyPreState(o, a, snap);
        else if (t <= end)       applyFrame(o, a, snap, easeOut((t - start) / a.duration));
        else                     applyPostState(o, a, snap);
      });
      c.requestRenderAll();

      if (t >= duration) {
        resetAll(objs, snapshots); c.requestRenderAll();
        opts.onComplete?.();
        resolve();
      } else {
        requestAnimationFrame(tick);
      }
    }
    requestAnimationFrame(tick);
  });
}
